// ANGULAR CORE
import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

// RXJS
import { Subject, Subscription, combineLatest, firstValueFrom } from 'rxjs';
import { map, switchMap, filter, takeUntil, tap } from 'rxjs/operators';

// FIREBASE
import { getFunctions, httpsCallable } from 'firebase/functions';

// FIREBASE AUTH
import { Auth } from '@angular/fire/auth';

// MODELOS
import { Iglesia } from '../models/iglesia.model';
import { CuentaBancaria, CUENTA_DEFAULT } from '../models/financial-config.model';

// SERVICIOS FINANCIEROS
import { BankAccountService } from '../services/bank-account.service';

// SERVICIOS PERSONALIZADOS
import { ConfigService, MinisteriosRolesConfig, MinisterioConfig } from '../services/config.service';
import { DataService } from '../services/data';
import { NotificationService } from '../services/notification.service';
import { SpinnerService } from '../services/spinner.service';
import { ToastService } from '../services/toast.service';

// PRIMENG
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { TableModule } from 'primeng/table';
import { CheckboxModule } from 'primeng/checkbox';
import { RippleModule } from 'primeng/ripple';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { ToolbarModule } from 'primeng/toolbar';
import { ListboxModule } from 'primeng/listbox';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import { FloatLabelModule } from 'primeng/floatlabel';
import { SelectModule } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';
import { MessageModule } from 'primeng/message';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

// FONTAWESOME
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { SignaturePadComponent } from '../signature-pad/signature-pad';
import { Eliminados } from '../eliminados/eliminados';
import { Firestore, doc, updateDoc, deleteDoc } from '@angular/fire/firestore';



@Component({
  selector: 'app-settings',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    TableModule,
    CheckboxModule,
    InputNumberModule,
    RippleModule,
    ToggleSwitchModule,
    ToolbarModule,
    ListboxModule,
    FontAwesomeModule,
    ToastModule,
    ConfirmDialogModule,
    FloatLabelModule,
    SelectModule,
    TooltipModule,
    MessageModule,
    SignaturePadComponent,
    Eliminados,
    TranslateModule
  ],
  templateUrl: './settings.html',
  styleUrl: './settings.css'
})
export class Settings implements OnInit, OnDestroy {
  ministeriosRolesConfig: MinisteriosRolesConfig = { ministerios: [], rolesGenerales: [] };
  private subscriptions = new Subscription();
  private destroy$ = new Subject<void>();


  displayMinistryModal: boolean = false;
  currentMinistry: MinisterioConfig = { id: '', nombre: '', roles: [], activo: true, orden: 0, icono: '' };
  isEditingMinistry: boolean = false;

  isAdminOrPastor: boolean = false;
  showSignaturePad = false;

  newRoleForMinistry: string = '';
  newGeneralRole: string = '';
  selectedSize: "small" | undefined;
  iglesia: Iglesia = {
    churchId: '',
    churchName: '',
    churchMission: '',
    churchZone: '',
    churchAddress: '',
    churchPhone: '',
    pastorName: '',
    pastorEmail: '',
    pastorPhone: '',
    fechaCreacion: null,
    fechaCobro: null,
    pastorUid: '',
    startNew: false
  };

  churchId: string = '';
  firmaGuardadaUrl: string | null = null;

  // Gestín de Cuentas Bancarias
  showBankDialog: boolean = false;
  showBankForm: boolean = false;
  cuentasBancarias: CuentaBancaria[] = [CUENTA_DEFAULT];
  currentBankAccount: CuentaBancaria = { ...CUENTA_DEFAULT };
  editingBankAccount: boolean = false;
  private originalManejarBancos: boolean = false;

  // Funcionalidades controladas desde panel-mmm
  mostrarCuentasBancarias: boolean = true;

  // Estado del periodo abierto (para bloquear activación de bancos)
  hayPeriodoAbierto: boolean = false;
  periodoPendienteYM: string = '';
  /** Evita guardar al hidratar el toggle desde Firestore. */
  private configFormReady = false;

  constructor(
    private configService: ConfigService,
    private dataService: DataService,
    private notificationService: NotificationService,
    private spinnerService: SpinnerService,
    private toastService: ToastService,
    private confirmationService: ConfirmationService,
    private router: Router,
    private firestore: Firestore,
    private bankAccountService: BankAccountService,
    private auth: Auth,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService
  ) { }

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translate.instant(key, params);
  }

  private generateSlug(name: string): string {
    return name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
  }

  ngOnInit(): void {
    // Check Permissions
    this.subscriptions.add(
      this.dataService.userRoles$.subscribe(roles => {
        const allowedRoles = ['Admin', 'Pastor'];
        this.isAdminOrPastor = roles.some(role => allowedRoles.includes(role));
        if (!this.isAdminOrPastor) {
          this.router.navigate(['/dashboard/main']);
        }
      })
    );

    // Feature: mostrar/ocultar cuentas bancarias según config de misión
    this.subscriptions.add(
      this.dataService.mostrarCuentasBancarias$.subscribe(val => {
        this.mostrarCuentasBancarias = val;
      })
    );

    this.suscripciones();

    // Montar el pad de firma tras el primer render (reduce conflictos con extensiones/canvas)
    requestAnimationFrame(() => {
      this.showSignaturePad = true;
      this.cdr.markForCheck();
    });
  }

  private suscripciones() {
    this.subscriptions.add(
      this.dataService.churchId$.pipe(
        filter(Boolean),
        tap(churchId => this.churchId = churchId as string),
        // cuando ya tenemos churchId, encadenamos a dataChurch$
        switchMap(() =>
          this.dataService.dataChurch$.pipe(
            filter((iglesia): iglesia is Iglesia => !!iglesia) // ya no usamos .data
          )
        )
      ).subscribe(iglesia => this.llenarDatosIglesia(iglesia))
    );

    this.subscriptions.add(
      this.dataService.churchSignatureUrl$
        .pipe(takeUntil(this.destroy$))
        .subscribe(url => {
          this.firmaGuardadaUrl = url;
        })
    );

    this.subscriptions.add(
      this.dataService.openPeriodo$.subscribe(periodo => {
        const currentYM = new Date().toISOString().slice(0, 7);
        const openYM = periodo ? ((periodo as any).id || (periodo as any).ym || '') : '';
        this.hayPeriodoAbierto = !!(openYM && openYM < currentYM);
        this.periodoPendienteYM = this.hayPeriodoAbierto ? openYM : '';
      })
    );
  }

  llenarDatosIglesia(iglesia: Iglesia) {
    this.configFormReady = false;
    this.iglesia = { ...iglesia };
    this.originalManejarBancos = !!iglesia.manejarBancos;
    if (iglesia.manejarBancos) {
      this.loadCuentasBancarias();
    }
    setTimeout(() => {
      this.configFormReady = true;
    }, 0);
  }

  onManejarBancosChange(): void {
    if (!this.configFormReady) return;
    void this.guardarConfiguracion();
  }

  async loadCuentasBancarias(): Promise<void> {
    if (!this.churchId) return;
    try {
      const cuentas = await this.bankAccountService.getCuentasBancarias(this.churchId);
      this.cuentasBancarias = cuentas;
      this.dataService.updateCuentasBancarias(cuentas);
    } catch (error) {
      console.error('Error cargando cuentas bancarias:', error);
    }
  }

  openBankDialog() {
    this.loadCuentasBancarias();
    this.showBankForm = false;
    this.showBankDialog = true;
  }

  openAddBankAccount() {
    this.currentBankAccount = {
      id: `cuenta-${Date.now()}`,
      nombre: '',
      tipo: 'banco',
      esDefault: false,
      activo: true,
      orden: this.cuentasBancarias.length + 1
    };
    this.editingBankAccount = false;
    this.showBankForm = true;
  }

  openEditBankAccount(cuenta: CuentaBancaria) {
    this.currentBankAccount = { ...cuenta };
    this.editingBankAccount = true;
    this.showBankForm = true;
  }

  async saveBankAccount() {
    if (!this.churchId) {
      this.toastService.showError(this.t('SETTINGS.TOAST_CHURCH_ID_UNAVAILABLE'));
      return;
    }
    if (!this.currentBankAccount.nombre) {
      this.toastService.showError(this.t('SETTINGS.TOAST_BANK_NAME_REQUIRED'));
      return;
    }
    try {
      this.spinnerService.show();
      const userId = this.auth.currentUser?.email || 'system';
      if (this.editingBankAccount) {
        await this.bankAccountService.updateCuentaBancaria(this.churchId, this.currentBankAccount, userId);
        this.toastService.showSuccess(this.t('SETTINGS.TOAST_BANK_UPDATED'));
      } else {
        await this.bankAccountService.addCuentaBancaria(this.churchId, this.currentBankAccount, userId);
        this.toastService.showSuccess(this.t('SETTINGS.TOAST_BANK_ADDED'));
      }
      await this.loadCuentasBancarias();
      this.currentBankAccount = { ...CUENTA_DEFAULT };
      this.showBankForm = false;
    } catch (error: any) {
      this.toastService.showError(error.message || this.t('SETTINGS.TOAST_BANK_SAVE_ERROR'));
    } finally {
      this.spinnerService.hide();
    }
  }

  async deleteBankAccount(cuenta: CuentaBancaria) {
    if (!this.churchId) return;

    try {
      const saldos = await firstValueFrom(this.dataService.saldosCuentas$);
      if (saldos[cuenta.id] && saldos[cuenta.id] > 0) {
        this.toastService.showError(this.t('SETTINGS.TOAST_BANK_DELETE_BLOCKED_BALANCE', { name: cuenta.nombre }));
        return;
      }
    } catch (error) {
       console.error('Error al verificar saldo antes de eliminar:', error);
       // Continuamos, podría no haber documento de saldos aún
    }

    this.confirmationService.confirm({
      message: this.t('SETTINGS.CONFIRM_DELETE_BANK_MSG', { name: cuenta.nombre }),
      header: this.t('SETTINGS.CONFIRM_DELETE_TITLE'),
      icon: 'pi pi-trash',
      acceptLabel: this.t('COMMON.DELETE'),
      rejectLabel: this.t('COMMON.CANCEL'),
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-secondary',
      accept: async () => {
        try {
          this.spinnerService.show();
          const userId = this.auth.currentUser?.email || 'system';
          await this.bankAccountService.deleteCuentaBancaria(this.churchId, cuenta.id, userId);
          await this.loadCuentasBancarias();
          this.toastService.showSuccess(this.t('SETTINGS.TOAST_BANK_DELETED'));
        } catch (error: any) {
          this.toastService.showError(error.message || this.t('SETTINGS.TOAST_BANK_DELETE_ERROR'));
        } finally {
          this.spinnerService.hide();
        }
      }
    });
  }

  async toggleBankAccount(cuenta: CuentaBancaria) {
    if (!this.churchId) return;
    try {
      const userId = this.auth.currentUser?.email || 'system';
      await this.bankAccountService.toggleCuentaBancaria(this.churchId, cuenta.id, userId);
      await this.loadCuentasBancarias();
      this.toastService.showSuccess(cuenta.activo ? this.t('SETTINGS.TOAST_BANK_DEACTIVATED') : this.t('SETTINGS.TOAST_BANK_ACTIVATED'));
    } catch (error: any) {
      this.toastService.showError(error.message || this.t('SETTINGS.TOAST_UPDATE_ERROR'));
    }
  }

  async guardarDatosIglesia() {
    if (!this.churchId) {
      this.toastService.showError(this.t('SETTINGS.TOAST_ERR_NO_CHURCH'));
      return;
    }

    try {
      this.spinnerService.show();

      const churchDocRef = doc(this.firestore, `iglesias/${this.churchId}`);

      // Solo actualizar campos editables
      await updateDoc(churchDocRef, {
        churchAddress: this.iglesia.churchAddress,
        pastorName: this.iglesia.pastorName,
        pastorPhone: this.iglesia.pastorPhone || '',
        churchPhone: this.iglesia.churchPhone || ''
      });

      this.toastService.showSuccess(this.t('SETTINGS.TOAST_CHURCH_INFO_UPDATED'));
    } catch (error: any) {
      this.toastService.showError(this.t('SETTINGS.TOAST_SAVE_ERROR_DETAIL', { detail: error.message || this.t('SETTINGS.TRY_AGAIN') }));
    } finally {
      this.spinnerService.hide();
    }
  }

  async guardarConfiguracion() {
    if (!this.churchId) {
      this.toastService.showError(this.t('SETTINGS.TOAST_ERR_NO_CHURCH'));
      return;
    }

    const turningOn = this.iglesia.manejarBancos && !this.originalManejarBancos;
    const turningOff = !this.iglesia.manejarBancos && this.originalManejarBancos;

    // Validación: si se activa manejarBancos, el periodo anterior debe estar cerrado
    // (doble seguro: ya no debería llegar aquí porque el toggle está oculto, pero por si acaso)
    if (turningOn && this.hayPeriodoAbierto) {
      this.iglesia = { ...this.iglesia, manejarBancos: false };
      this.cdr.detectChanges();
      this.toastService.showError(this.t('SETTINGS.TOAST_MUST_CLOSE_REPORT_BEFORE_BANKS', { period: this.periodoPendienteYM }));
      return;
    }

    if (turningOff) {
      const saldos = await firstValueFrom(this.dataService.saldosCuentas$);
      const cuentas = this.dataService.getCuentasBancarias();
      let hasBalance = false;
      
      for (const cuenta of cuentas) {
        if (cuenta.tipo === 'banco' && saldos[cuenta.id] && saldos[cuenta.id] > 0) {
          hasBalance = true;
          break;
        }
      }

      if (hasBalance) {
        setTimeout(() => {
          this.iglesia = { ...this.iglesia, manejarBancos: true };
          this.cdr.detectChanges();
        });
        this.toastService.showError(this.t('SETTINGS.TOAST_CANNOT_DISABLE_BANKS_WITH_BALANCE'));
        return;
      }
    }

    try {
      this.spinnerService.show();

      const churchDocRef = doc(this.firestore, `iglesias/${this.churchId}`);

      // Actualizar configuración de manejar bancos
      await updateDoc(churchDocRef, {
        manejarBancos: this.iglesia.manejarBancos || false
      });

      // Actualizar DataService inmediatamente
      this.dataService.updateManejarBancos(this.iglesia.manejarBancos || false);

      // Si se activó manejarBancos por primera vez, inicializar saldosCuentas
      if (turningOn) {
        try {
          const functions = getFunctions();
          const initFn = httpsCallable(functions, 'inicializarSaldosCuentas');
          // Usar el saldo disponible real calculado en Movimientos (0 si aún no se ha visitado)
          const saldoActual = this.dataService.getSaldoDisponibleReal();
          const result = await initFn({ churchId: this.churchId, saldoInicial: saldoActual });
          const initData = (result as any).data;
          if (initData?.alreadyExists) {
            this.toastService.showInfo(this.t('SETTINGS.TOAST_BANKS_ENABLED_ALREADY_INIT'));
          } else if (initData?.success) {
            this.toastService.showSuccess(
              this.t('SETTINGS.TOAST_BANKS_ENABLED_WITH_INITIAL_BALANCE', { amount: Number(saldoActual).toFixed(2) })
            );
          } else {
            this.toastService.showSuccess(this.t('SETTINGS.TOAST_CONFIG_UPDATED'));
          }
        } catch (initErr: any) {
          // Si falla la inicialización de saldos, no bloqueamos (puede hacerse después)
          console.warn('No se pudieron inicializar los saldos:', initErr?.message);
          this.toastService.showSuccess(this.t('SETTINGS.TOAST_CONFIG_SAVED_VISIT_MOVEMENTS'));
        }
        this.originalManejarBancos = true;
      } else if (turningOff) {
        try {
          // Eliminar documento de saldosCuentas
          const saldosCuentasRef = doc(this.firestore, `iglesias/${this.churchId}/movimientos/saldosCuentas`);
          await deleteDoc(saldosCuentasRef);
          this.toastService.showSuccess(this.t('SETTINGS.TOAST_BANKS_DISABLED_BALANCES_RESET'));
        } catch (delErr: any) {
          console.warn('No se pudo eliminar el documento saldosCuentas:', delErr?.message);
          this.toastService.showSuccess(this.t('SETTINGS.TOAST_BANKS_DISABLED_CLEANUP_WARN'));
        }
        this.originalManejarBancos = false;
      } else {
        this.toastService.showSuccess(this.t('SETTINGS.TOAST_CONFIG_UPDATED'));
      }
    } catch (error: any) {
      this.toastService.showError(this.t('SETTINGS.TOAST_SAVE_ERROR_DETAIL', { detail: error.message || this.t('SETTINGS.TRY_AGAIN') }));
    } finally {
      this.spinnerService.hide();
    }
  }

  addMinistry(): void {
    if (!this.isAdminOrPastor) {
      this.notificationService.showError(this.t('SETTINGS.TOAST_NO_PERMISSION'));
      return;
    }
    this.isEditingMinistry = false;
    this.currentMinistry = { id: '', nombre: '', roles: [], activo: true, orden: (this.ministeriosRolesConfig?.ministerios?.length || 0) + 1, icono: '' };
    this.displayMinistryModal = true;
  }

  editMinistry(ministerio: MinisterioConfig): void {
    if (!this.isAdminOrPastor) {
      this.notificationService.showError(this.t('SETTINGS.TOAST_NO_PERMISSION'));
      return;
    }
    this.isEditingMinistry = true;
    this.currentMinistry = { ...ministerio, roles: [...ministerio.roles] };
    this.displayMinistryModal = true;
  }

  async saveMinistry(): Promise<void> {
    const churchId = this.dataService.getChurchId();
    if (!churchId) {
      this.toastService.showError(this.t('SETTINGS.TOAST_ERR_NO_CHURCH'));
      return;
    }

    if (!this.currentMinistry.nombre || this.currentMinistry.nombre.trim() === '') {
      this.toastService.showError(this.t('SETTINGS.TOAST_MINISTRY_NAME_REQUIRED'));
      return;
    }

    this.spinnerService.show();

    if (!this.isEditingMinistry) {
      this.currentMinistry.id = this.generateSlug(this.currentMinistry.nombre);
    }
    this.currentMinistry.roles = this.currentMinistry.roles.filter(rol => rol.trim() !== '');

    if (this.currentMinistry.orden === null || this.currentMinistry.orden === undefined || isNaN(this.currentMinistry.orden)) {
      this.currentMinistry.orden = (this.ministeriosRolesConfig?.ministerios?.length || 0) + 1;
    } else {
      this.currentMinistry.orden = Number(this.currentMinistry.orden);
    }

    try {
      const functions = getFunctions();
      if (this.isEditingMinistry) {
        const updateMinisterioFunction = httpsCallable(functions, 'updateMinisterio');
        await updateMinisterioFunction({ churchId, updatedMinisterio: this.currentMinistry })
          .then(() => {
            this.toastService.showSuccess(this.t('SETTINGS.TOAST_MINISTRY_UPDATED'));
            this.displayMinistryModal = false;
          })
          .catch((error) => {
            this.toastService.showError(this.t('SETTINGS.TOAST_MINISTRY_UPDATE_ERROR', { detail: error.message || error }))
          })
          .finally(() => {
            this.spinnerService.hide();
          });
      } else {
        if (this.ministeriosRolesConfig?.ministerios?.some(m => m.id === this.currentMinistry.id)) {
          this.toastService.showError(this.t('SETTINGS.TOAST_MINISTRY_ID_EXISTS', { id: this.currentMinistry.id }));
          return;
        }
        const addMinisterioFunction = httpsCallable(functions, 'addMinisterio');
        await addMinisterioFunction({ churchId, newMinisterio: this.currentMinistry })
          .then(() => {
            this.toastService.showSuccess(this.t('SETTINGS.TOAST_MINISTRY_ADDED'));
            this.displayMinistryModal = false;
          })
          .catch((error) => {
            this.toastService.showError(this.t('SETTINGS.TOAST_MINISTRY_SAVE_ERROR', { detail: error.message || error }))
          })
          .finally(() => {
            this.spinnerService.hide();
          });
      }
    } catch (error: any) {
      this.toastService.showError(this.t('SETTINGS.TOAST_MINISTRY_SAVE_ERROR', { detail: error.message || error }))
    } finally {
      this.spinnerService.hide();
    }
  }

  async onMinistryStatusChange(ministerio: MinisterioConfig, nuevoEstado: boolean) {
    if (!this.isAdminOrPastor) {
      this.notificationService.showError(this.t('SETTINGS.TOAST_NO_PERMISSION'));
      // Revertir el estado visualmente
      ministerio.activo = !nuevoEstado;
      return;
    }

    const churchId = this.dataService.getChurchId();
    if (!churchId) {
      this.notificationService.showError(this.t('SETTINGS.TOAST_ERR_NO_CHURCH'));
      ministerio.activo = !nuevoEstado;
      return;
    }

    // Actualizar el objeto local primero para una respuesta visual inmediata
    ministerio.activo = nuevoEstado;

    this.spinnerService.show();

    try {
      const functions = getFunctions();
      const updateMinisterioFunction = httpsCallable(functions, 'setMinisterioActivo');
      await updateMinisterioFunction({ churchId, id: ministerio.id, activo: ministerio.activo })
        .then(() => {
          this.toastService.showSuccess(this.t('SETTINGS.TOAST_MINISTRY_UPDATED'));
        })
        .catch((error) => {
          ministerio.activo = !nuevoEstado;
          this.toastService.showError(this.t('SETTINGS.TOAST_MINISTRY_UPDATE_ERROR', { detail: error.message || error }));
        })
        .finally(() => {
          this.spinnerService.hide();
        });
    } catch (error: any) {
      ministerio.activo = !nuevoEstado;
      this.toastService.showError(this.t('SETTINGS.TOAST_MINISTRY_UPDATE_ERROR', { detail: error.message || error }));
    } finally {
      this.spinnerService.hide();
    }
  }

  async deleteMinistry(ministerio: MinisterioConfig): Promise<void> {
    const churchId = this.dataService.getChurchId();
    if (!churchId) {
      this.toastService.showError(this.t('SETTINGS.TOAST_ERR_NO_CHURCH'));
      return;
    }
    this.confirmationService.confirm({
      message: this.t('SETTINGS.CONFIRM_DELETE_MINISTRY_MSG'),
      header: this.t('SETTINGS.CONFIRM_DELETE_TITLE'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.t('SETTINGS.CONFIRM_DELETE_ACCEPT'),
      rejectLabel: this.t('COMMON.CANCEL'),
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-secondary',
      accept: async () => {
        try {
          this.spinnerService.show();
          const functions = getFunctions();
          const deleteMinisterioCallable = httpsCallable(functions, 'deleteMinisterio');
          await deleteMinisterioCallable({ churchId, ministerioId: ministerio.id });
          this.toastService.showSuccess(this.t('SETTINGS.TOAST_MINISTRY_DELETED'));
          this.spinnerService.hide();
        } catch (error) {
          this.toastService.showError(this.t('SETTINGS.TOAST_MINISTRY_DELETE_ERROR'));
          this.spinnerService.hide();
        }
      },
      reject: () => {

      }
    });
  }

  addRoleToCurrentMinistry(): void {
    if (this.currentMinistry) {
      if (!this.currentMinistry.roles) {
        this.currentMinistry.roles = [];
      }
      this.currentMinistry.roles.push('');
    }
  }

  removeRoleFromCurrentMinistry(index: number): void {
    if (this.currentMinistry && this.currentMinistry.roles && this.currentMinistry.roles.length > 0 && index >= 0 && index < this.currentMinistry.roles.length) {
      this.currentMinistry.roles.splice(index, 1);
    }
  }

  async addGeneralRole(): Promise<void> {
    if (!this.isAdminOrPastor) {
      this.notificationService.showError(this.t('SETTINGS.TOAST_NO_PERMISSION'));
      return;
    }
    const churchId = this.dataService.getChurchId();
    if (!churchId) {
      this.notificationService.showError(this.t('SETTINGS.TOAST_ERR_NO_CHURCH'));
      return;
    }
    if (this.newGeneralRole && this.newGeneralRole.trim() !== '') {
      const roleToAdd = this.newGeneralRole.trim();
      if (this.ministeriosRolesConfig?.rolesGenerales?.includes(roleToAdd)) {
        this.notificationService.showError(this.t('SETTINGS.TOAST_GENERAL_ROLE_EXISTS', { role: roleToAdd }));
        return;
      }
      this.spinnerService.show();
      try {
        await this.configService.addGeneralRole(churchId, roleToAdd);
        this.notificationService.showSuccess(this.t('SETTINGS.TOAST_GENERAL_ROLE_ADDED'));
        this.newGeneralRole = '';
      } catch (error: any) {
        this.notificationService.showError(this.t('SETTINGS.TOAST_GENERAL_ROLE_ADD_ERROR', { detail: error.message || error }));
      } finally {
        this.spinnerService.hide();
      }
    } else {
      this.notificationService.showInfo(this.t('SETTINGS.TOAST_GENERAL_ROLE_NAME_REQUIRED'));
    }
  }

  async removeGeneralRole(role: string): Promise<void> {
    const churchId = this.dataService.getChurchId();
    if (!churchId) {
      this.toastService.showError(this.t('SETTINGS.TOAST_ERR_NO_CHURCH'));
      return;
    }
    this.confirmationService.confirm({
      message: this.t('SETTINGS.CONFIRM_DELETE_MINISTRY_MSG'),
      header: this.t('SETTINGS.CONFIRM_DELETE_TITLE'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.t('SETTINGS.CONFIRM_DELETE_ACCEPT'),
      rejectLabel: this.t('COMMON.CANCEL'),
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-secondary',
      accept: async () => {
        try {
          this.spinnerService.show();
          const functions = getFunctions();
          const deleteMinisterioCallable = httpsCallable(functions, 'deleteMinisterio');
          await deleteMinisterioCallable({ churchId, ministerioId: role });
          this.toastService.showSuccess(this.t('SETTINGS.TOAST_MINISTRY_DELETED'));
          this.spinnerService.hide();
        } catch (error) {
          this.toastService.showError(this.t('SETTINGS.TOAST_MINISTRY_DELETE_ERROR'));
          this.spinnerService.hide();
        }
      },
      reject: () => {

      }
    });
  }

  trackByFn(index: number, item: any): any {
    return item.id || index;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.subscriptions.unsubscribe();
  }

}
